import { getTimeout } from '@/helpers/globals'
import type { Ref } from 'vue'
import { computed, onBeforeUnmount, watch } from 'vue'
import { parseDateTime } from '@/utils/date'
import type { DeliveryTimeframeFragment } from '@/graphql/api'
import type { UseQueryResponse } from '@urql/vue'

export function useDeliveryDate<T>(
    response: UseQueryResponse<T>,
    extractor: (data: T) => DeliveryTimeframeFragment | undefined
): Readonly<Ref<DeliveryTimeframeFragment | undefined>> {
    const deliveryDate = computed(() => {
        const data = response.data.value

        if (!data) {
            return undefined
        }

        return extractor(data)
    })

    const timeout = getTimeout()

    if (timeout) {
        const { setTimeout, clearTimeout } = timeout

        let timeoutId: number | undefined = undefined

        const clear = () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
                timeoutId = undefined
            }
        }

        watch(
            deliveryDate,
            (newData) => {
                clear()

                if (newData) {
                    timeoutId = setTimeout(
                        () => {
                            response.executeQuery({
                                requestPolicy: 'network-only',
                            })
                        },
                        parseDateTime(newData.validUntil).getTime() -
                            new Date().getTime()
                    )
                }
            },
            { immediate: true }
        )

        onBeforeUnmount(clear)
    }

    return deliveryDate
}
